import React from 'react';
import { useController, FieldValues, UseControllerProps } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import { Dropdown } from 'components/_new/Dropdown';
import CustomLabel from './Label';

interface Props<T extends FieldValues> extends UseControllerProps<T> {
	choiceList: string[];
	description: string | null;
	label?: string;
	labelClassName?: string;
	underLabel?: string;
	required?: boolean;
	links?: string[] | null;
	onChangeValue?: (value: any) => void;
}
const ValidateSingleChoice = <T extends FieldValues>({
	name,
	description,
	label,
	labelClassName,
	underLabel,
	choiceList,
	control,
	required = false,
	rules,
	links,
	onChangeValue,
}: Props<T>) => {
	const { t } = useTranslation();

	const {
		field,
		fieldState: { error },
	} = useController({
		name,
		control,
		rules: { required: required ? `${label} ${t('misc.forms.isRequired')}` : false, ...rules },
	});

	const dropdownOptions = choiceList.map((item) => ({
		label: item,
		value: item,
	}));

	return (
		<div>
			{label && (
				<CustomLabel name={name} required={required} className={labelClassName}>
					<Trans
						components={
							links
								? links.map((link) => (
										// eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
										<a href={link} target="_blank" rel="noreferrer" className="p-link" />
								  ))
								: undefined
						}
					>
						{label}
					</Trans>
				</CustomLabel>
			)}
			{underLabel && <p className="mt-0 mb-2 px-3 text-sm text-muted">{underLabel}</p>}
			{(choiceList?.length || 0) > 6 ? (
				<Dropdown
					value={field.value}
					options={dropdownOptions}
					onChange={(e) => {
						if (onChangeValue) {
							onChangeValue(e.value);
						}
						field.onChange(e.value);
					}}
					placeholder={description || t('misc.dropdownPleaseSelect')}
					className={error ? 'p-invalid' : ''}
				/>
			) : (
				<div className="px-3">
					<div className="mb-3">
						<Trans
							components={
								links
									? links.map((link) => (
											// eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
											<a href={link} target="_blank" rel="noreferrer" className="p-link" />
									  ))
									: undefined
							}
						>
							{description}
						</Trans>
					</div>

					{(choiceList || []).map((item, index) => (
						<div key={item} className="field-radiobutton">
							<RadioButton
								inputRef={index === 0 ? field.ref : undefined}
								inputId={field.name}
								name={field.name}
								value={item}
								onChange={(e) => {
									if (onChangeValue) {
										onChangeValue(e.value);
									}
									field.onChange(e.value);
								}}
								checked={field.value === item}
								className={classNames({ 'p-invalid': !!error })}
							/>

							<label
								onClick={() => {
									if (onChangeValue) {
										onChangeValue(item);
									}
									field.onChange(item);
								}}
								className={classNames({ 'p-error': !!error }, 'cursor-pointer')}
							>
								{item}
							</label>
						</div>
					))}
				</div>
			)}

			{error && <small className="p-error font-bold ml-3">{error.message}</small>}
		</div>
	);
};

export default ValidateSingleChoice;
